'use client';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { MapPin, Phone, Question, User } from '@phosphor-icons/react';

interface Data {
  type?: string;
  path?: string;
  title: string;
  subCategory?: {
    subcat: string;
    list: {
      label: string;
      desc: string;
      path: string;
      hidden: boolean;
    }[];
  }[];
}

const MobileNav = ({
  data,
  closeSidebar,
  authEnabled = true,
}: {
  data: Data[];
  closeSidebar: () => void;
  authEnabled?: boolean;
}) => {
  const [openHeadings, setOpenHeadings] = useState<string[]>([]);
  const [openSubHeadings, setOpenSubHeadings] = useState<string[]>([]);
  const router = useRouter();

  const handleToggleDropdown = (heading: string) => {
    setOpenHeadings((prevOpenHeadings) =>
      prevOpenHeadings.includes(heading)
        ? prevOpenHeadings.filter((openHeading) => openHeading !== heading)
        : [...prevOpenHeadings, heading]
    );
  };

  const handleToggleSubDropdown = (subHeading: string) => {
    setOpenSubHeadings((prevOpenSubHeadings) =>
      prevOpenSubHeadings.includes(subHeading)
        ? prevOpenSubHeadings.filter(
            (openSubHeading) => openSubHeading !== subHeading
          )
        : [...prevOpenSubHeadings, subHeading]
    );
  };

  useEffect(() => {
    setOpenHeadings([]);
    setOpenSubHeadings([]);
  }, [closeSidebar]);

  return (
    <div className="w-full pb-3 divide-y divide-[#ECEFF3]">
      {data?.map((item, index) => (
        <div
          onClick={() => {
            if (item?.type === 'single') {
              router.push(item?.path ?? '/');
              closeSidebar();
              setOpenHeadings([]);
              setOpenSubHeadings([]);
            }
          }}
          key={index}
          className={`flex flex-col bg-white px-2 md:border-none py-3 ${
            !openHeadings.includes(item?.title) ? 'md:flex-shrink-0 ' : ''
          }`}
        >
          {/* Main category dropdown */}
          <div
            onClick={() => handleToggleDropdown(item?.title)}
            className={`flex w-full items-center justify-between px-3 py-2 rounded-full `}
          >
            <p className="font-semibold text-[14px] font-proxima_font text-[#0A0A0A] cursor-pointer">
              {item?.title}
            </p>

            {item?.type !== 'single' && (
              <span
                className={`chevron ${openHeadings.includes(item?.title) ? 'up' : 'down'}`}
              />
            )}
          </div>

          {/* Subcategory dropdowns */}
          <ul
            className={`flex flex-col pl-3 transition-all  duration-200 font-medium font-proxima_font ${
              openHeadings.includes(item?.title)
                ? 'overflow-visible max-h-[3000px] mt-3 '
                : 'overflow-hidden max-h-0'
            }`}
          >
            {item?.subCategory?.map((subCatItem, subCatIndex) => (
              <div className="border-t border-[#ECEFF3]" key={subCatIndex}>
                <div
                  onClick={() => handleToggleSubDropdown(subCatItem.subcat)}
                  className="flex w-full items-center justify-between px-3 py-4 rounded-full cursor-pointer"
                >
                  <p className="font-semibold text-[14px] text-[#0A0A0A] font-proxima_font">
                    {subCatItem?.subcat}
                  </p>
                  <span
                    className={`chevron ${openSubHeadings.includes(subCatItem?.subcat) ? 'up' : 'down'}`}
                  />
                </div>

                <ul
                  className={`flex flex-col gap-2 border-[#ECEFF3] px-5 transition-all duration-200 ${
                    openSubHeadings.includes(subCatItem?.subcat)
                      ? 'overflow-visible max-h-screen py-4 border-t '
                      : 'overflow-hidden max-h-0'
                  }`}
                >
                  {subCatItem?.list
                    ?.filter((link) => link?.hidden !== true)
                    ?.map((link, linkIndex) => (
                      <li
                        key={linkIndex}
                        className={`transition-all bg-off-white px-2 h-[85px] rounded-md duration-300 ${
                          openSubHeadings.includes(subCatItem?.subcat)
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-4'
                        }`}
                      >
                        <Link
                          onClick={() => {
                            closeSidebar();
                            setOpenHeadings([]);
                            setOpenSubHeadings([]);
                          }}
                          className=" text-[14px] flex flex-col mt-2 gap-1 "
                          href={link?.path ?? ''}
                          prefetch={false}
                          aria-label="Subcategory links"
                        >
                          <p className="text-[14px] text-[#0A0A0A] tracking-tight font-semibold font-proxima_font">
                            {link?.label}
                          </p>
                          <p className="text-[12px] font-proxima_font text-[#666D80] line-clamp-2">
                            {link?.desc}
                          </p>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </ul>
        </div>
      ))}

      <div className="flex flex-col bg-off-white">
        {authEnabled !== false && (
          <Link
            onClick={closeSidebar}
            href={'/sign-in'}
            className="flex items-center px-4 py-[16px] border-b border-[#DFE1E7] gap-[12px]"
          >
            <User size={20} color="#666D80" />
            <p className="text-[14px] font-semibold font-proxima_font text-[#666D80]">
              Login
            </p>
          </Link>
        )}
        <Link
          onClick={closeSidebar}
          href={'/store-locator'}
          className="nav-store-locator flex items-center px-4 py-[16px] border-b border-[#DFE1E7] gap-[12px]"
        >
          <MapPin size={20} color="#666D80" />
          <p className="text-[14px] font-semibold font-proxima_font text-[#666D80]">
            Find A Dealer
          </p>
        </Link>
        <Link
          onClick={closeSidebar}
          href={'/support'}
          className="flex items-center px-4 py-[16px] border-b border-[#DFE1E7] gap-[12px]"
        >
          <Question size={20} color="#666D80" />
          <p className="text-[14px] font-semibold font-proxima_font text-[#666D80]">
            Help Centre
          </p>
        </Link>
        <Link
          onClick={closeSidebar}
          href={'/support/contact-us'}
          className="flex items-center px-4 py-[16px] border-b border-[#DFE1E7] gap-[12px]"
        >
          <Phone size={20} color="#666D80" />
          <p className="text-[14px] font-semibold font-proxima_font text-[#666D80]">
            Contact Us
          </p>
        </Link>
      </div>
    </div>
  );
};

export default MobileNav;
